import { DateTime } from "luxon";
import { clientMutationOptions } from "./controller/clientMutation";

import { PagedResult } from "../DTOs/Common/PagedResult";
import { AddGeoProductionMiningMovementDTO } from "../DTOs/Geology/AddGeoProductionMiningMovementDTO";
import { GeoCrushingLoaderDTO } from "../DTOs/Geology/GeoCrushingLoaderDTO";
import GeoMillDTO from "../DTOs/Geology/GeoMillDTO";
import { GeoMiningMovementDTO } from "../DTOs/Geology/GeoMiningMovementDTO";
import { GeoMiningProductionExcavatorDTO } from "../DTOs/Geology/GeoMiningProductionExcavatorDTO";
import { GeoMiningProductionTruckDTO } from "../DTOs/Geology/GeoMiningProductionTruckDTO";
import { GeoOreBlockDTO } from "../DTOs/Geology/GeoOreBlockDTO";
import { GeoOreBlockEntryDTO } from "../DTOs/Geology/GeoOreBlockEntryDTO";
import { GeoOreBlockGeofenceDTO } from "../DTOs/Geology/GeoOreBlockGeofenceDTO";
import { GeoOreBlockEntryDetailDTO } from "../DTOs/Geology/GeoOreBlockEntryDetailDTO";
import { GeoOreTallyMiningProductionListEntryDTO } from "../DTOs/Geology/GeoOreTallyMiningProductionListEntryDTO";
import { GeoOreTruckDTO } from "../DTOs/Geology/GeoOreTruckDTO";
import { GeoProductionEntryDTO } from "../DTOs/Geology/GeoProductionEntryDTO";
import { GeoRehandleDTO } from "../DTOs/Geology/GeoRehandleDTO";
import { GeoRehandleDiggerDTO } from "../DTOs/Geology/GeoRehandleDiggerDTO";
import { GeoRehandleTruckingTruckDTO } from "../DTOs/Geology/GeoRehandleTruckingTruckDTO";
import { GeoRehandleGroupBound, GetGeoRehandleGroupsPagedResultDTO } from "../DTOs/Geology/GeoRehandleGroupDTO";
import { UpdateOreTallyMiningProductionEntryRequest } from "../DTOs/Geology/UpdateOreTallyMiningProductionEntryRequestDTO";
import { DataTableFilteredRequest } from "../components/DataTable/TableHook";
import { ajaxController, formatDatesForDateOnlyModel } from "./controller/AjaxController";
import GeoDrillingEntryDTO from "../DTOs/Geology/GeoDrillingEntryDTO";
import { TripleShift } from "../types/Shift";
import { LocationDTO } from "../DTOs/Production/LocationDTO";
import { GetGeoOreBlocksFilteredRequest } from "../DTOs/Geology/GetGeoOreBlocksFilteredRequest";
import { GeoOreTallyPagedResult } from "../DTOs/Geology/GeoOreTallyPagedResult";
import { GetGeoRehandlesPagedResultDTO } from "../DTOs/Geology/GetGeoRehandlesPagedResultDTO";
import { GeoRehandleMutationResponseDTO } from "../DTOs/Geology/GeoRehandleMutationResponseDTO";
import { GetGeoMiningMvtTruckSummaryPagedResultDTO } from "../DTOs/Geology/GetGeoMiningMvtTruckSummaryPagedResultDTO";
import { GetOreTallyMiningProductionResultDto } from "../DTOs/Geology/GetOreTallyMiningProductionResultDTO";
import { GetOreTallyGeologyProductionResultDTO } from "../DTOs/Geology/GetOreTallyGeologyProductionResultDTO";

class GeologyAjax {
  private _ajaxController = ajaxController;
  private _geologyDataEndpoint = "/Geology";

  async getGeoOreTrucks(filters: DataTableFilteredRequest) {
    return await this._ajaxController.get<PagedResult<GeoOreTruckDTO>>(`${this._geologyDataEndpoint}/geology-ore-trucks`, filters);
  }

  async deleteGeoOreTruck(geoOreTruckId: number) {
    return await this._ajaxController.delete(`${this._geologyDataEndpoint}/geology-ore-trucks/${geoOreTruckId}`);
  }

  async getGeoMiningMovementById(geoMiningMovementId: number) {
    return await this._ajaxController.get<GeoMiningMovementDTO>(`${this._geologyDataEndpoint}/geology-mining-movements/${geoMiningMovementId}`, {});
  }

  async getGeoMiningMovement(shiftDate: DateTime, dayOrNight: TripleShift, diggerId: number, pitStageId: number) {
    return await this._ajaxController.get<GeoMiningMovementDTO | undefined>(
      `${this._geologyDataEndpoint}/geology-mining-movements/${shiftDate.toISODate()}/${dayOrNight[0]}/${diggerId}/${pitStageId}`,
      {});
  }

  async getGeoMiningMovements(filters: DataTableFilteredRequest) {
    return await this._ajaxController.get<PagedResult<GeoMiningMovementDTO>>(
      `${this._geologyDataEndpoint}/geology-mining-movements`,
      filters
    );
  }

  async addGeoMiningMovement(data: GeoMiningMovementDTO) {
    return await this._ajaxController.post(`${this._geologyDataEndpoint}/geology-mining-movements`, data);
  }

  async addGeoProductionMiningMovement(data: AddGeoProductionMiningMovementDTO) {
    return await this._ajaxController.post<AddGeoProductionMiningMovementDTO, GeoMiningMovementDTO>(
      `${this._geologyDataEndpoint}/geology-mining-movements/production`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async updateGeoMiningMovement(data: GeoMiningMovementDTO) {
    return await this._ajaxController.put<GeoMiningMovementDTO>(
      `${this._geologyDataEndpoint}/geology-mining-movements/${data.geoMiningMvtId}`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async deleteFilterFromMovementEntry(geoMiningMovementId: number, oreBlockToDelete: string) {
    return await this._ajaxController.delete(`${this._geologyDataEndpoint}/geology-mining-movements/${geoMiningMovementId}/${oreBlockToDelete}`);
  }

  async deleteGeoMiningMovement(geoMiningMovementId: number) {
    return await this._ajaxController.delete(`${this._geologyDataEndpoint}/geology-mining-movements/${geoMiningMovementId}`);
  }

  async getGeoProductionEntries(geoMiningMovementId: number) {
    return await this._ajaxController.get<PagedResult<GeoProductionEntryDTO>>(`${this._geologyDataEndpoint}/geology-production-entries/${geoMiningMovementId}`, {});
  }

  async updateGeoProductionEntry(data: GeoProductionEntryDTO) {
    return await this._ajaxController.put(`${this._geologyDataEndpoint}/geology-production-entries/${data.geoOreTruckId}`, data);
  }

  async addGeoProductionEntry(data: GeoProductionEntryDTO) {
    return await this._ajaxController.post<GeoProductionEntryDTO, GeoProductionEntryDTO>(`${this._geologyDataEndpoint}/geology-production-entries`, data);
  }

  // Note: its own call, mirroring updateBlastPatternGeofence. The ore blocks table edits inline and
  // rebuilds GeoOreBlockEntryDTO from cell values, so putting the fence on that payload would erase the
  // shape on every unrelated edit.
  updateGeoOreBlockGeofence = (geoOreBlockId: number, geofencePolygon: string | null) => {
    return this._ajaxController.put<GeoOreBlockGeofenceDTO, GeoOreBlockGeofenceDTO>(
      `${this._geologyDataEndpoint}/geology-ore-blocks/${geoOreBlockId}/geofence`,
      { geofencePolygon }
    );
  }

  // Note: accepts every unchecked row on one shift's Rehandle or Crushing screen. Scoped to the shift AND
  // the crushing split, so accepting on one screen cannot clear the other.
  verifyAllGeoRehandles = (shiftDate: DateTime, dayNightAfternoon: TripleShift, isCrushing: boolean) => {
    return this._ajaxController.post<undefined, number>(
      `${this._geologyDataEndpoint}/geo-rehandle/verify-all/${shiftDate.toISODate()}/${dayNightAfternoon[0]}/${isCrushing}`,
      undefined
    );
  }

  // Note: accepts one inferred rehandle/crushing row as correct without changing its values, mirroring
  // diggingAjax.verifyTruckLoads. Editing a row also clears the flag, but only as a side effect of changing
  // something - which is no use for a row the FMS got right.
  verifyGeoRehandle = (geoRehandleId: number) => {
    return this._ajaxController.post<undefined, boolean>(
      `${this._geologyDataEndpoint}/geo-rehandle/verify/${geoRehandleId}`,
      undefined
    );
  }

  async getGeoOreBlock(geoOreBlockId: number) {
    return await this._ajaxController.get<GeoOreBlockEntryDTO>(`${this._geologyDataEndpoint}/geology-ore-blocks/${geoOreBlockId}`, {});
  }

  async getGeoOreBlocks(filters: GetGeoOreBlocksFilteredRequest) {
    return await this._ajaxController.get<PagedResult<GeoOreBlockDTO>>(`${this._geologyDataEndpoint}/geology-ore-blocks`, filters);
  }

  async getGeoOreBlockEntry(geoOreBlockEntryId: number) {
    return await this._ajaxController.get<GeoOreBlockEntryDTO>(`${this._geologyDataEndpoint}/geology-ore-block-entries/${geoOreBlockEntryId}`, {});
  }

  async getGeoOreBlockEntries(filters: GetGeoOreBlocksFilteredRequest) {
    return await this._ajaxController.get<PagedResult<GeoOreBlockEntryDTO>>(`${this._geologyDataEndpoint}/geology-ore-block-entries`, filters);
  }

  async geoGeoProductionEntriesForOreBlock(geoOreBlockId: number) {
    return await this._ajaxController.get<PagedResult<GeoOreBlockEntryDetailDTO>>(`${this._geologyDataEndpoint}/geology-production-entries-with-ore-block/${geoOreBlockId}`, {});
  }

  async getUncompletedGeoOreBlockEntries(filters: GetGeoOreBlocksFilteredRequest) {
    return await this._ajaxController.get<PagedResult<GeoOreBlockEntryDTO>>(`${this._geologyDataEndpoint}/geology-ore-block-entries/uncompleted`, filters);
  }

  async addGeoOreBlock(data: GeoOreBlockEntryDTO) {
    return await this._ajaxController.post(`${this._geologyDataEndpoint}/geology-ore-blocks`, data);
  }

  async updateGeoOreBlock(data: GeoOreBlockEntryDTO) {
    return await this._ajaxController.put(`${this._geologyDataEndpoint}/geology-ore-blocks/${data.id}`, data);
  }

  async deleteGeoOreBlock(geoOreBlockId: number) {
    return await this._ajaxController.delete(`${this._geologyDataEndpoint}/geology-ore-blocks/${geoOreBlockId}`);
  }

  async getGeoRehandles(date: DateTime, dayNightAfternoon: TripleShift, request: object = {}) {
    return await this._ajaxController.get<GetGeoRehandlesPagedResultDTO>(`${this._geologyDataEndpoint}/geology-rehandles/${date.toISODate()}/${dayNightAfternoon[0]}`, request);
  }

  async getGeoCrushingEntries(date: DateTime, dayNightAfternoon: TripleShift, request: object = {}) {
    return await this._ajaxController.get<GetGeoRehandlesPagedResultDTO>(`${this._geologyDataEndpoint}/geology-crushing/${date.toISODate()}/${dayNightAfternoon[0]}`, request);
  }

  async getGeoRehandleGroups(date: DateTime, dayNightAfternoon: TripleShift, isCrushing: boolean, request: object = {}) {
    return await this._ajaxController.get<GetGeoRehandleGroupsPagedResultDTO>(
      `${this._geologyDataEndpoint}/geology-rehandle-groups/${date.toISODate()}/${dayNightAfternoon[0]}/${isCrushing}`,
      request
    );
  }

  async getGeoRehandleGroupConstituents(
    date: DateTime, dayNightAfternoon: TripleShift, isCrushing: boolean, groupKey: string) {
    return await this._ajaxController.get<PagedResult<GeoRehandleDTO>>(
      `${this._geologyDataEndpoint}/geology-rehandle-groups/${date.toISODate()}/${dayNightAfternoon[0]}/${isCrushing}/constituents`,
      { groupKey }
    );
  }

  private groupQuery(groupKey: string, bound?: GeoRehandleGroupBound) {
    const params = new URLSearchParams({ groupKey });
    if (bound) params.set('expectedCount', String(bound.expectedCount));
    if (bound?.maxGeoRehandleId != null) params.set('maxGeoRehandleId', String(bound.maxGeoRehandleId));
    return params.toString();
  }

  async verifyGeoRehandleGroup(
    date: DateTime, dayNightAfternoon: TripleShift, isCrushing: boolean, groupKey: string, bound?: GeoRehandleGroupBound) {
    return await this._ajaxController.post<undefined, number>(
      `${this._geologyDataEndpoint}/geology-rehandle-groups/${date.toISODate()}/${dayNightAfternoon[0]}/${isCrushing}/verify?${this.groupQuery(groupKey, bound)}`,
      undefined
    );
  }

  async deleteGeoRehandleGroup(
    date: DateTime, dayNightAfternoon: TripleShift, isCrushing: boolean, groupKey: string, bound?: GeoRehandleGroupBound) {
    return await this._ajaxController.delete<number>(
      `${this._geologyDataEndpoint}/geology-rehandle-groups/${date.toISODate()}/${dayNightAfternoon[0]}/${isCrushing}?${this.groupQuery(groupKey, bound)}`
    );
  }

  async addGeoRehandle(data: GeoRehandleDTO, date: DateTime, dayNightAfternoon: TripleShift, clientId?: string) {
    return await this._ajaxController.post<GeoRehandleDTO, GeoRehandleMutationResponseDTO>(
      `${this._geologyDataEndpoint}/geology-rehandles/${date.toISODate()}/${dayNightAfternoon[0]}`,
      data,
      undefined,
      false,
      clientMutationOptions(clientId)
    );
  }

  // Note: no date-only transform, unlike the other geology saves. It cut movementTimeUtc down to its date,
  // which the server then read as a new time outside the shift and refused.
  async updateGeoRehandle(data: GeoRehandleDTO, clientId?: string) {
    return await this._ajaxController.put<GeoRehandleDTO, GeoRehandleMutationResponseDTO>(
      `${this._geologyDataEndpoint}/geology-rehandles/${data.geoRehandleId}`,
      data,
      undefined,
      clientMutationOptions(clientId)
    );
  }

  async deleteGeoRehandle(geoRehandleId: number, clientId?: string) {
    return await this._ajaxController.delete<GeoRehandleMutationResponseDTO>(
      `${this._geologyDataEndpoint}/geology-rehandles/${geoRehandleId}`,
      clientMutationOptions(clientId)
    );
  }

  async getGeoOreTallyListEntries(dateFrom: DateTime, filters: DataTableFilteredRequest) {
    return await this._ajaxController.get<GeoOreTallyPagedResult>(`${this._geologyDataEndpoint}/geology-ore-tallys/${dateFrom.toISODate()}`,
      filters,
      undefined,
      formatDatesForDateOnlyModel
    );
  }

  async getGeoOreTallyMiningProductionEntries(dateFrom: DateTime, dayOrNight: TripleShift, filters: DataTableFilteredRequest) {
    return await this._ajaxController.get<GetOreTallyMiningProductionResultDto>(`${this._geologyDataEndpoint}/geology-ore-tallys/mining-production/${dateFrom.toISODate()}/${dayOrNight[0]}`, filters);
  }

  async updateGeoOreMiningProductionEntry(data: UpdateOreTallyMiningProductionEntryRequest) {
    return await this._ajaxController.post<UpdateOreTallyMiningProductionEntryRequest, GeoOreTallyMiningProductionListEntryDTO>(`${this._geologyDataEndpoint}/geology-ore-tallys/mining-production`, data);
  }

  async getGeoOreTallyGeologyProductionEntries(dateFrom: DateTime, dayOrNight: TripleShift, filters: DataTableFilteredRequest) {
    return await this._ajaxController.get<GetOreTallyGeologyProductionResultDTO>(`${this._geologyDataEndpoint}/geology-ore-tallys/geology-production/${dateFrom.toISODate()}/${dayOrNight[0]}`, filters);
  }

  async getGeoMiningProductionExcavators(shiftId: number, pitId: number) {
    return await this._ajaxController.get<PagedResult<GeoMiningProductionExcavatorDTO>>(
      `${this._geologyDataEndpoint}/mining-production-excavators/${shiftId}/${pitId}`,
      {}
    );
  }

  async getProductionTruckingTrucks(date: DateTime) {
    return await this._ajaxController.get<PagedResult<GeoMiningProductionTruckDTO>>(
      `${this._geologyDataEndpoint}/production-trucking-trucks/${date.toISODate()}`,
      {}
    );
  }

  async getRehandleDiggers(date: DateTime, dayNightAfternoon: TripleShift) {
    return await this._ajaxController.get<PagedResult<GeoRehandleDiggerDTO>>(
      `${this._geologyDataEndpoint}/rehandle-diggers/${date.toISODate()}/${dayNightAfternoon[0]}`,
      {}
    );
  }

  async getRehandleTruckingTrucks(date: DateTime) {
    return await this._ajaxController.get<PagedResult<GeoRehandleTruckingTruckDTO>>(
      `${this._geologyDataEndpoint}/trucking-trucks/${date.toISODate()}`,
      {}
    );
  }

  async getCrushingLoaders(date: DateTime, dayNightAfternoon: TripleShift) {
    return await this._ajaxController.get<PagedResult<GeoCrushingLoaderDTO>>(
      `${this._geologyDataEndpoint}/loaders/${date.toISODate()}/${dayNightAfternoon[0]}`,
      {}
    );
  }

  async getRehandleMovementFromLocations(date: DateTime) {
    return await this._ajaxController.get<PagedResult<LocationDTO>>(
      `${this._geologyDataEndpoint}/rehandle/locations/movement-from/${date.toISODate()}`,
      {}
    );
  }

  async getRehandleMovementToLocations(date: DateTime) {
    return await this._ajaxController.get<PagedResult<LocationDTO>>(
      `${this._geologyDataEndpoint}/rehandle/locations/movement-to/${date.toISODate()}`,
      {}
    );
  }

  async getCrushingMovementFromLocations(date: DateTime) {
    return await this._ajaxController.get<PagedResult<LocationDTO>>(
      `${this._geologyDataEndpoint}/crushing/locations/movement-from/${date.toISODate()}`,
      {}
    );
  }

  async getCrushingMovementToLocations(date: DateTime) {
    return await this._ajaxController.get<PagedResult<LocationDTO>>(
      `${this._geologyDataEndpoint}/crushing/locations/movement-to/${date.toISODate()}`,
      {}
    );
  }

  async getGeologyMillingEntries(filters: DataTableFilteredRequest) {
    return await this._ajaxController.get<PagedResult<GeoMillDTO>>(`${this._geologyDataEndpoint}/milling`,
      filters
    );
  }

  async getGeologyMillingDistinctMillIds() {
    return await this._ajaxController.get<PagedResult<number>>(`${this._geologyDataEndpoint}/milling/mills/ids`, {});
  }

  async addGeologyMillingEntry(data: GeoMillDTO) {
    return await this._ajaxController.post<GeoMillDTO, GeoMillDTO>(`${this._geologyDataEndpoint}/milling`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async updateGeologyMillingEntry(data: GeoMillDTO) {
    return await this._ajaxController.put<GeoMillDTO>(`${this._geologyDataEndpoint}/milling/${data.id}`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async deleteGeologyMillingEntry(geologyMillingEntryId: number) {
    return await this._ajaxController.delete(`${this._geologyDataEndpoint}/milling/${geologyMillingEntryId}`);
  }

  async getGeoMiningMovementsSummary(
    shiftDate: DateTime,
    dayOrNight: TripleShift,
    filters: DataTableFilteredRequest
  ) {
    return await this._ajaxController.get<GetGeoMiningMvtTruckSummaryPagedResultDTO>(
      `${this._geologyDataEndpoint}/geology-mining-movements-summary/${shiftDate.toISODate()}/${dayOrNight[0]}`,
      filters
    );
  }

  async getGeoDrillingEntries(filters: DataTableFilteredRequest) {
    return await this._ajaxController.get<PagedResult<GeoDrillingEntryDTO>>(`${this._geologyDataEndpoint}/geology-drillings/entries`,
      filters,
      undefined,
      formatDatesForDateOnlyModel
    );
  }

  async saveGeoDrillingEntry(data: GeoDrillingEntryDTO) {
    return await this._ajaxController.post<GeoDrillingEntryDTO, GeoDrillingEntryDTO>(`${this._geologyDataEndpoint}/geology-drillings/entries`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async deleteGeoDrillingEntry(date: DateTime) {
    return await this._ajaxController.delete(`${this._geologyDataEndpoint}/geology-drillings/entries/${date.toISODate()}`);
  }
}

export const geologyAjax = new GeologyAjax();
